import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-[2000] overflow-hidden rounded-md border bg-[#111] px-3 py-1.5 text-sm text-white shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface SimpleTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  enabled?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  forceMount?: boolean;
  trigger?: 'hover' | 'always';
}

const SimpleTooltip = ({
  content,
  children,
  className,
  enabled = true,
  side,
  sideOffset,
  forceMount,
  trigger = 'hover',
}: SimpleTooltipProps) => {
  if (!enabled) {
    return <>{children}</>;
  }

  // Check if children is a disabled button/input and wrap it in a span if it is
  const child = React.Children.only(children) as React.ReactElement;
  const isDisabled = child.props.disabled === true;
  const triggerElement = isDisabled ? (
    <span className="inline-block w-full">{children}</span>
  ) : (
    children
  );

  const shouldForceMount = trigger === 'always' || forceMount;

  return (
    <Tooltip open={trigger === 'always' ? true : undefined}>
      <TooltipTrigger asChild>{triggerElement}</TooltipTrigger>
      <TooltipContent
        className={className}
        side={side}
        sideOffset={sideOffset}
        {...(shouldForceMount ? { forceMount: true } : {})}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
};

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, SimpleTooltip };
